import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { cn } from "lib/utils";
import { sortApartment } from "../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { RootState } from "../../../../redux/store";
import { ApartmentSortMode } from "../../../../types/apartments";

const SORT_MODES: ApartmentSortMode[] = [
  // #3016: the tenant's own order (then price ascending), the default.
  "RECOMMENDED",
  "PRICE_ASC",
  "PRICE_DESC",
  "NAME_ASC",
  "NAME_DESC",
];

const SORT_LABEL_KEYS: Record<ApartmentSortMode, string> = {
  RECOMMENDED: "apartment.sort.recommended",
  PRICE_ASC: "apartment.sort.price_asc",
  PRICE_DESC: "apartment.sort.price_desc",
  NAME_ASC: "apartment.sort.name_asc",
  NAME_DESC: "apartment.sort.name_desc",
};

/**
 * Sort dropdown for the apartment/venue list, shared by the room-rental and
 * venue-reservation booking pages (both sort the same apartment slice).
 */
const ApartmentSortMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sortMode = useSelector((state: RootState) => state.apartment.sortMode);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-4 flex h-11 items-center gap-1 rounded-md border border-foreground/20 bg-white px-4 py-2 text-sm text-black hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {t("apartment.sort.title")}
          <ChevronsUpDown className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SORT_MODES.map((mode) => (
          <DropdownMenuItem
            key={mode}
            onSelect={() => dispatch(sortApartment(mode))}
          >
            <span className={cn(sortMode === mode && "font-semibold")}>
              {t(SORT_LABEL_KEYS[mode])}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ApartmentSortMenu;
